import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { SelectOption } from '../types/select-options';
import { SelectOptionColorPicker } from './SelectOptionColorPicker';
import { COLOR_MAPPINGS } from '../../../../utils/colorUtils';
import { createPortal } from 'react-dom';

export interface SelectOptionItemProps {
  option: SelectOption;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: (newLabel: string) => void;
  onColorChange?: (newColor: string) => void;
}

export function SelectOptionItem({
  option,
  isSelected,
  onSelect,
  onEdit,
  onColorChange,
}: SelectOptionItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(option.label);
  // Create a local color state to preview color changes before saving
  const [previewColor, setPreviewColor] = useState(option.color);

  if (isEditing) {
    // Handle save changes
    const handleSave = () => {
      // Skip if nothing changed
      if (editValue.trim() === option.label && previewColor === option.color) {
        setIsEditing(false);
        return;
      }

      // Update label if changed
      if (editValue.trim() !== option.label) {
        onEdit(editValue);
      }

      // Update color if changed
      if (previewColor !== option.color && onColorChange) {
        onColorChange(previewColor);
      }

      setIsEditing(false);
    };

    // Handle cancel - reset to original values
    const handleCancel = () => {
      setEditValue(option.label);
      setPreviewColor(option.color);
      setIsEditing(false);
    };

    return (
      <div
        className="flex flex-col p-2 bg-white shadow-md rounded-md"
        onClick={e => e.stopPropagation()}
        // Prevent any clicks inside this container from closing the editor
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Input row with color picker */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1">
            <input
              type="text"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              className="w-full px-2 py-1 text-sm rounded bg-white border border-gray-200 focus:outline-none focus:border-blue-300 transition-all"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
                if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
            />
          </div>

          {/* Color picker outside the dropdown to make it appear above */}
          {onColorChange && (
            <div
              className="relative"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <div
                className={`w-6 h-6 rounded-full ${COLOR_MAPPINGS.dot[previewColor] || COLOR_MAPPINGS.dot.gray} cursor-pointer`}
                data-color-target={option.id}
                onClick={e => {
                  e.stopPropagation();
                  document.dispatchEvent(
                    new CustomEvent('open-color-picker', { detail: { id: option.id } })
                  );
                }}
              />

              {/* We'll add a separate color picker portal in the component */}
            </div>
          )}
        </div>

        {/* Button row */}
        <div className="flex justify-end gap-2">
          <button
            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
            onClick={handleSave}
          >
            Save
          </button>
        </div>

        {/* External color picker that will appear through a portal */}
        {onColorChange &&
          createPortal(
            <SelectOptionColorPicker
              option={{ ...option, color: previewColor }}
              onColorSelect={color => {
                setPreviewColor(color);
              }}
              position="right"
              targetId={option.id}
            />,
            document.body
          )}
      </div>
    );
  }

  const { color } = option;

  return (
    <div
      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${isSelected ? 'bg-blue-50 text-blue-600' : ''} flex items-center justify-between group`}
    >
      <button className="flex-1 text-left flex items-center" onClick={onSelect}>
        <div className="flex items-center gap-2">
          <div
            className={`w-3 h-3 rounded-full ${COLOR_MAPPINGS.dot[color] || COLOR_MAPPINGS.dot.gray}`}
          ></div>
          <span>{option.label}</span>
        </div>
      </button>

      <div className="flex items-center space-x-1">
        <button
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 p-1"
          onClick={e => {
            e.stopPropagation();
            setIsEditing(true);
          }}
        >
          <Pencil size={14} />
        </button>
      </div>
    </div>
  );
}
